import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Items from "../Aside/Components/Items";
import Minimize from "./Components/Minimize";
import { determineActiveIndex, shouldUseMiniAside } from "./asideState";
import { useNotificationCounts } from "../../context/NotificationCountContext";
import LiquidGlass from "../Style/LiquidGlass";

const ASIDE_SCROLL_STORAGE_KEY = "asideScrollTop";

export default function Container({
  activeIndex: activeIndexProp,
  alwaysMini: alwaysMiniProp,
} = {}) {
  const { notifCount, ticketCount } = useNotificationCounts();
  const { pathname } = useLocation();
  const scrollContainerRef = useRef(null);
  const activeIndex =
    typeof activeIndexProp === "number"
      ? activeIndexProp
      : determineActiveIndex(pathname);
  const alwaysMini =
    typeof alwaysMiniProp === "boolean"
      ? alwaysMiniProp
      : shouldUseMiniAside(pathname);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem("asideCollapsed");
    return stored === "true"; // default is expanded when nothing stored
  });

  const effectiveCollapsed = alwaysMini ? true : isCollapsed;
  const minimizeTooltipText = effectiveCollapsed ? "باز کردن" : "بستن";

  const handleToggle = () => {
    if (alwaysMini) return; // disable toggle
    setIsCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== "undefined") {
        localStorage.setItem("asideCollapsed", String(next));
      }
      return next;
    });
  };

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const savedScrollTop = sessionStorage.getItem(ASIDE_SCROLL_STORAGE_KEY);
    const nextScrollTop = Number(savedScrollTop);

    if (
      scrollContainerRef.current &&
      savedScrollTop !== null &&
      Number.isFinite(nextScrollTop)
    ) {
      scrollContainerRef.current.scrollTop = nextScrollTop;
    }
  }, []);

  const handleScroll = () => {
    if (typeof window === "undefined" || !scrollContainerRef.current) return;
    sessionStorage.setItem(
      ASIDE_SCROLL_STORAGE_KEY,
      String(scrollContainerRef.current.scrollTop),
    );
  };

  return (
    <LiquidGlass tag="aside" rounded="2xl" className={`hidden p-3 md:flex flex-col gap-6 rounded-2xl drop-shadow-md border border-zi-blue-light dark:border-none bg-zi-bg2-light dark:bg-zi-inputbg-dark/50 ${
        effectiveCollapsed ? "w-[62px]" : "w-[200px]"
      }`} childrenClassName="flex flex-col gap-6 overflow-hidden">
      <div className="w-fit"
        data-tooltip-id="aside-minimize-tooltip"
        data-tooltip-content={minimizeTooltipText}
      >
        <Minimize
          onToggle={handleToggle}
          isCollapsed={effectiveCollapsed}
          alwaysMini={alwaysMini}
        />
      </div>
      {typeof document !== "undefined" &&
        createPortal(
          <Tooltip
            id="aside-minimize-tooltip"
            place="left"
            positionStrategy="fixed"
            style={{ zIndex: 9999 }}
            className="!py-1 !px-2 !text-xs !font-thin !backdrop-blur !bg-cyan-600 !rounded-lg"
          />,
          document.body,
        )}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="overflow-y-auto flex flex-col gap-6 p-0"
      >
        <div className="flex flex-col gap-3">
          <Items
            svg_name="dashboard"
            title="میز کار"
            isActive={activeIndex === 1}
            href="/Dashboard"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="purchase"
            title="خرید چالش جدید"
            isActive={activeIndex === 2}
            href="/purchase"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="challenge"
            title="حساب‌ها و چالش‌ها"
            isActive={activeIndex === 3}
            href="/Challenge"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="trade"
            title="معامله"
            isActive={activeIndex === 4}
            href="/trade/account"
            showTitle={!effectiveCollapsed}
          />
          {/* <Items
            svg_name="calculator"
            title="ماشین حساب"
            isActive={activeIndex === 5}
            href="/calculator"
            showTitle={!effectiveCollapsed}
          /> */}
          <Items
            svg_name="transaction"
            title="تراکنش‌ها"
            isActive={activeIndex === 6}
            href="/Transaction/Withdraw"
            showTitle={!effectiveCollapsed}
          />
        </div>

        <div className="flex flex-col gap-3">
          <p
            className={`text-xs font-normal text-nowrap text-zi-blue-light/50 dark:text-white/50 ${
              effectiveCollapsed && "opacity-0"
            }`}
          >
            {!effectiveCollapsed ? "بخش هدایا" : "."}
          </p>

          {/* <Items svg_name="competition" title="جوایز و مسابقات" isActive={activeIndex === 7} href="/Dashboard" showTitle={!effectiveCollapsed} /> */}
          <Items
            svg_name="invite"
            title="دعوت از دوستان"
            isActive={activeIndex === 8}
            href="/invite"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="discount"
            title="کد تخفیف"
            isActive={activeIndex === 9}
            href="/discount"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="certificate"
            title="گواهی‌نامه‌ها"
            isActive={activeIndex === 10}
            href="/certificate/general"
            showTitle={!effectiveCollapsed}
          />
          {/* <Items svg_name="point" title="امتیازات" isActive={activeIndex === 11} href="/Dashboard" showTitle={!effectiveCollapsed} /> */}
        </div>

        <div className="flex flex-col gap-3">
          <p
            className={`text-xs font-normal text-nowrap text-zi-blue-light/50 dark:text-white/50 ${
              effectiveCollapsed && "opacity-0"
            }`}
          >
            {!effectiveCollapsed ? "ناحیه کاربری" : "."}
          </p>
          <Items
            svg_name="profile"
            title="حساب کاربری"
            isActive={activeIndex === 12}
            href="/profile/information"
            showTitle={!effectiveCollapsed}
          />
          <Items
            svg_name="notification"
            title="اعلانات"
            isActive={activeIndex === 13}
            href="/notification/all"
            showTitle={!effectiveCollapsed}
            notifCount={notifCount}
            haveNotifications={
              notifCount && Number(notifCount) > 0 ? true : false
            }
          />
          <Items
            svg_name="support"
            title="پشتیبانی"
            isActive={activeIndex === 14}
            href="/support"
            showTitle={!effectiveCollapsed}
            notifCount={ticketCount}
            haveNotifications={
              ticketCount && Number(ticketCount) > 0 ? true : false
            }
          />
          <Items
            svg_name="rules"
            title="قوانین"
            isActive={activeIndex === 15}
            href="/rule/general"
            showTitle={!effectiveCollapsed}
          />
        </div>
      </div>
    </LiquidGlass>
  );
}
